import { Offer, Settings } from '../../../domain'

type FallbackSource = Settings | Offer

/**
 * Determine if the fallback source is an Offer object
 */
const isOffer = (source: FallbackSource): source is Offer => {
  return 'id' in source && typeof source.id === 'number'
}

/**
 * Get the translated value for a given key with fallback support
 */
export const getTranslatedValue = (
  key: string,
  translationsMetaobjectData: Record<string, string> | null,
  generalTranslations: Record<string, string> | null,
  fallbackSource?: FallbackSource,
  fallback?: string,
  context?: string
): string => {
  if (!key) {
    return ''
  }

  const contextLabel = context ? `${context}: ` : ''

  // Priority 1: Metaobject translations
  if (translationsMetaobjectData?.[key]) {
    return translationsMetaobjectData[key]
  }

  console.warn(`${contextLabel}Translation for ${key} not found in offer metaobject`)

  // Priority 2: General metaobject translations
  if (generalTranslations?.[key]) {
    return generalTranslations[key]
  }

  const fallbackSourceType = fallbackSource ? (isOffer(fallbackSource) ? 'Offer' : 'Settings') : 'Offer'
  console.warn(`${contextLabel}Translation for ${key} not found in general metaobject, using fallback from ${fallbackSourceType}`)

  // Priority 3: Fallback source (settings/offer)
  if (fallbackSource?.[key as keyof FallbackSource]) {
    const value = fallbackSource[key as keyof FallbackSource]
    if (value && typeof value === 'string') {
      return value
    }
  }

  console.warn(`${contextLabel}Translation for ${key} not found in fallback source`)

  // Priority 4: Hard-coded fallback
  if (fallback !== undefined) {
    console.warn(`${contextLabel}Translation for ${key} not found, using hard-coded fallback`)
    return fallback
  }

  console.warn(`${contextLabel}Translation for ${key} not found at all!`)

  return ''
}
